import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Typography, Paper, Tabs, Tab, Divider
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FaHome } from 'react-icons/fa';
import { login, register, googleAuthUrl } from '../../services/auth';
import { useApp } from '../../context/AppContext';

export default function Login() {
    const [tab, setTab] = useState(0); // 0 = Entrar, 1 = Criar conta
    const [email, setEmail] = useState('admin@autogrow.com');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [cep, setCep] = useState('');
    const [logradouro, setLogradouro] = useState('');
    const [numero, setNumero] = useState('');
    const [complemento, setComplemento] = useState('');
    const [bairro, setBairro] = useState('');
    const [cidade, setCidade] = useState('');
    const [estado, setEstado] = useState('');
    const [loadingCep, setLoadingCep] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);

    // Busca endereço pelo CEP (ViaCEP) no formulário de registro.
    const handleCepChange = async (e) => {
        const value = e.target.value;
        setCep(value);
        const digits = value.replace(/\D/g, '');
        if (digits.length === 8) {
            setLoadingCep(true);
            try {
                const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
                const data = await res.json();
                if (!data.erro) {
                    setLogradouro(data.logradouro || '');
                    setBairro(data.bairro || '');
                    setCidade(data.localidade || '');
                    setEstado(data.uf || '');
                }
            } catch (_) { /* ignora erro de CEP */ } finally { setLoadingCep(false); }
        }
    };
    const navigate = useNavigate();
    const { login: doLogin, setProfile } = useApp();
    const [params] = useSearchParams();

    // Login via Google: o backend redireciona para /login?token=...
    useEffect(() => {
        const token = params.get('token');
        if (token) {
            localStorage.setItem('ag_token', token);
            doLogin();
            navigate('/dashboard');
        }
        if (params.get('verified') === '1') {
            toast.success('E-mail verificado! Agora você pode entrar.');
        }
    }, [params, doLogin, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            const data = await login(email, password);
            if (data.user) setProfile(data.user);
            doLogin();
            navigate('/dashboard');
        } catch (err) {
            const msg = err.response?.data?.error || 'Credenciais inválidas';
            setError(msg);
            if (err.response?.data?.needsVerification) setInfo('Verifique seu e-mail para ativar a conta.');
        } finally { setLoading(false); }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError(''); setInfo(''); setLoading(true);
        try {
            await register(email, password, {
                fullName,
                birthDate,
                address: { cep, logradouro, numero, complemento, bairro, cidade, estado }
            });
            setTab(0);
            setInfo('Conta criada! Enviamos um e-mail de verificação. Verifique sua caixa de entrada (ou o console do servidor em modo dev).');
            toast.success('Conta criada. Verifique seu e-mail.');
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao criar conta');
        } finally { setLoading(false); }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
            <Button
                variant="text"
                startIcon={<FaHome />}
                onClick={() => navigate('/')}
                sx={{ mb: 2, color: 'text.secondary' }}
            >
                Página Inicial
            </Button>
            <Paper sx={{ p: 4, width: 380 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                        component="img"
                        src="/logo.png"
                        alt="AutoGrow"
                        sx={{ width: 40, height: 40, mr: 1 }}
                    />
                    <Typography variant="h5">AutoGrow BR</Typography>
                </Box>

                <Tabs value={tab} onChange={(e, v) => { setTab(v); setError(''); setInfo(''); }} centered>
                    <Tab label="Entrar" />
                    <Tab label="Criar conta" />
                </Tabs>

                {tab === 0 && (
                    <form onSubmit={handleLogin}>
                        <TextField label="E-mail" fullWidth margin="normal" value={email}
                            onChange={(e) => setEmail(e.target.value)} />
                        <TextField label="Senha" type="password" fullWidth margin="normal" value={password}
                            onChange={(e) => setPassword(e.target.value)} />
                        {error && <Typography color="error" variant="body2">{error}</Typography>}
                        {info && <Typography color="info.main" variant="body2">{info}</Typography>}
                        <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
                            Entrar
                        </Button>
                    </form>
                )}

                {tab === 1 && (
                    <form onSubmit={handleRegister}>
                        <TextField label="Nome completo *" fullWidth margin="normal" value={fullName}
                            onChange={(e) => setFullName(e.target.value)} />
                        <TextField label="Data de nascimento *" type="date" fullWidth margin="normal"
                            InputLabelProps={{ shrink: true }} value={birthDate}
                            onChange={(e) => setBirthDate(e.target.value)} />
                        <TextField label="E-mail" fullWidth margin="normal" value={email}
                            onChange={(e) => setEmail(e.target.value)} />
                        <TextField label="Senha (mín. 6 caracteres)" type="password" fullWidth margin="normal" value={password}
                            onChange={(e) => setPassword(e.target.value)} />
                        <TextField label="CEP *" fullWidth margin="normal" value={cep}
                            onChange={handleCepChange}
                            placeholder="00000-000" />
                        <TextField label="Logradouro" fullWidth margin="normal" value={logradouro}
                            onChange={(e) => setLogradouro(e.target.value)} />
                        <TextField label="Número *" fullWidth margin="normal" value={numero}
                            onChange={(e) => setNumero(e.target.value)} />
                        <TextField label="Complemento" fullWidth margin="normal" value={complemento}
                            onChange={(e) => setComplemento(e.target.value)} />
                        <TextField label="Bairro" fullWidth margin="normal" value={bairro}
                            onChange={(e) => setBairro(e.target.value)} />
                        <TextField label="Cidade" fullWidth margin="normal" value={cidade}
                            onChange={(e) => setCidade(e.target.value)} />
                        <TextField label="UF" fullWidth margin="normal" value={estado}
                            onChange={(e) => setEstado(e.target.value)} />
                        {error && <Typography color="error" variant="body2">{error}</Typography>}
                        {info && <Typography color="info.main" variant="body2">{info}</Typography>}
                        <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
                            Criar conta
                        </Button>
                    </form>
                )}

                <Divider sx={{ my: 2 }}>ou</Divider>

                <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<GoogleIcon />}
                    onClick={() => { window.location.href = googleAuthUrl(); }}
                >
                    Entrar com Google
                </Button>
            </Paper>
        </Box>
    );
}
